import { useAppDispatch, useAppSelector } from 'app/hooks';
import SegmentedControl from 'components/inputs/segmented-control';
import AccordionSection from 'components/layout/accordion-section';
import { setPtacSelectionFilters } from 'features/ptac-selection';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import {
  isPtacElectricalFilterApplied,
  ptacElectricalDefaultsOf,
  ptacElectricalFilterFromOption,
  ptacElectricalFilterOptions,
  resolvePtacElectricalFilter
} from 'utils/ptac-electrical-filter';
import {
  enabledPtacFilterValues,
  PTAC_TERMINAL_TYPE_FILTER_OPTIONS,
  ptacFilterBadge,
  ptacFilterOptions,
  terminalTypeFilterValue,
  terminalTypeFromFilterValue,
  togglePtacFilterValue
} from 'utils/ptac-system-filters';
import FilterSection from './equipment-filter-section';

export default function PtacSystemFiltersSection() {
  const dispatch = useAppDispatch();
  const filters = useAppSelector((state) => state.ptacSelection.filters);
  const universe = useAppSelector(
    (state) => state.ptacSelection.systems.filterUniverse
  );
  const ptacPreferences = useAppSelector((state) => {
    const anchor = state.structure.existingSystems.find(isPtacExistingSystem);
    return anchor
      ? state.structure.recommendationSelectionsBySystem[anchor.uniqueId]
          ?.ptac ?? null
      : null;
  });
  const electricalDefaults = ptacElectricalDefaultsOf(ptacPreferences);
  const resolvedVoltage = resolvePtacElectricalFilter(
    filters.voltage,
    electricalDefaults.voltage
  );
  const resolvedAmperage = resolvePtacElectricalFilter(
    filters.amperage,
    electricalDefaults.amperage
  );

  const manufacturerOptions = ptacFilterOptions(
    'brand',
    universe.manufacturers
  );
  const seriesOptions = ptacFilterOptions('series', universe.series);
  const voltageOptions = ptacElectricalFilterOptions(
    universe.voltages,
    resolvedVoltage
  );
  const amperageOptions = ptacElectricalFilterOptions(
    universe.amperages,
    resolvedAmperage
  );

  return (
    <div data-testid='ptac-system-filters' className='flex flex-col'>
      <AccordionSection
        title='Unit Type'
        badge={filters.terminalType !== null ? 1 : null}
      >
        <SegmentedControl
          label='Unit type'
          options={PTAC_TERMINAL_TYPE_FILTER_OPTIONS}
          value={terminalTypeFilterValue(filters.terminalType)}
          onChange={(value) =>
            dispatch(
              setPtacSelectionFilters({
                terminalType: terminalTypeFromFilterValue(value)
              })
            )
          }
        />
      </AccordionSection>
      {voltageOptions.length >= 2 && (
        <AccordionSection
          title='Voltage'
          badge={
            isPtacElectricalFilterApplied(
              filters.voltage,
              electricalDefaults.voltage
            )
              ? 1
              : null
          }
        >
          <SegmentedControl
            label='Voltage'
            options={voltageOptions}
            value={resolvedVoltage ?? 'any'}
            onChange={(value) =>
              dispatch(
                setPtacSelectionFilters({
                  voltage: ptacElectricalFilterFromOption(value)
                })
              )
            }
          />
        </AccordionSection>
      )}
      {amperageOptions.length >= 2 && (
        <AccordionSection
          title='Amperage'
          badge={
            isPtacElectricalFilterApplied(
              filters.amperage,
              electricalDefaults.amperage
            )
              ? 1
              : null
          }
        >
          <SegmentedControl
            label='Amperage'
            options={amperageOptions}
            value={resolvedAmperage ?? 'any'}
            onChange={(value) =>
              dispatch(
                setPtacSelectionFilters({
                  amperage: ptacElectricalFilterFromOption(value)
                })
              )
            }
          />
        </AccordionSection>
      )}
      {manufacturerOptions.length >= 2 && (
        <AccordionSection
          title='Brand'
          badge={ptacFilterBadge(filters.manufacturers)}
        >
          <FilterSection
            options={manufacturerOptions}
            enabled={enabledPtacFilterValues(
              filters.manufacturers,
              universe.manufacturers
            )}
            onToggle={(value) =>
              dispatch(
                setPtacSelectionFilters({
                  manufacturers: togglePtacFilterValue(
                    filters.manufacturers,
                    universe.manufacturers,
                    value
                  )
                })
              )
            }
          />
        </AccordionSection>
      )}
      {seriesOptions.length >= 2 && (
        <AccordionSection
          title='Series'
          badge={ptacFilterBadge(filters.series)}
        >
          <FilterSection
            options={seriesOptions}
            enabled={enabledPtacFilterValues(filters.series, universe.series)}
            onToggle={(value) =>
              dispatch(
                setPtacSelectionFilters({
                  series: togglePtacFilterValue(
                    filters.series,
                    universe.series,
                    value
                  )
                })
              )
            }
          />
        </AccordionSection>
      )}
    </div>
  );
}
